// The correct clock hands (hour, minute endpoints) computed from the target time, for the reveal.
// Order corners around their centroid so the revealed polygon draws as the real figure rather than a
// self-crossing bowtie. The labels (3rd element) ride along untouched. Convex-safe, which covers the
// figures this is used for (parallelogram / quadrilateral from given + plotted corners).
const orderClosedBoundary = (points) => {
  const pts = (points || []).filter((p) => Array.isArray(p) && p.length >= 2);
  if (pts.length < 3) return pts;
  const cx = pts.reduce((s, p) => s + Number(p[0]), 0) / pts.length;
  const cy = pts.reduce((s, p) => s + Number(p[1]), 0) / pts.length;
  return [...pts].sort(
    (a, b) => Math.atan2(Number(a[1]) - cy, Number(a[0]) - cx) - Math.atan2(Number(b[1]) - cy, Number(b[0]) - cx)
  );
};

const clockRevealHands = (spec) => {
  const c = Array.isArray(spec.center) && spec.center.length >= 2 ? spec.center : [0.5, 0.5];
  const r = Number(spec.radius) || 0.4;
  const hour = (Number(spec.target_hour) || 0) % 12, minute = Number(spec.target_minute) || 0;
  const hb = hour * 30 + minute * 0.5, mb = minute * 6;
  const ep = (bearing, rr) => [c[0] + Math.sin((bearing * Math.PI) / 180) * rr, c[1] - Math.cos((bearing * Math.PI) / 180) * rr];
  return [ep(hb, r * 0.55), ep(mb, r * 0.92)];
};

// One valid example for a shade "metrics" question: scan for a w×h rectangle of grid cells whose
// area (w*h) and perimeter (2*(w+h)) match the targets, then map its cells to region ids. A
// rectangle exists for every (area, perimeter) the exam poses here; if none fits, returns [].
// When target_shape_count > 1 ("draw TWO rectangles, each with perimeter 18") lay one rectangle per
// required shape side by side with a one-column gap, honouring distinct_side_lengths.
const metricsRevealExample = (spec) => {
  const byCell = new Map((spec.regions || []).map((r) => [`${r.cell?.[0]},${r.cell?.[1]}`, String(r.id)]));
  const cols = (spec.regions || []).reduce((m, r) => Math.max(m, (r.cell?.[0] ?? 0) + 1), 0);
  const rows = (spec.regions || []).reduce((m, r) => Math.max(m, (r.cell?.[1] ?? 0) + 1), 0);
  const wantArea = spec.target_area, wantPerim = spec.target_perimeter;
  const dims = [];
  for (let h = 1; h <= rows; h++) {
    for (let w = 1; w <= cols; w++) {
      if (wantArea != null && w * h !== wantArea) continue;
      if (wantPerim != null && 2 * (w + h) !== wantPerim) continue;
      dims.push([w, h]);
    }
  }
  if (!dims.length) return [];

  const need = Math.max(1, Math.round(Number(spec.target_shape_count) || 1));
  const distinct = !!spec.distinct_side_lengths;
  const chosen = [];
  const usedSides = new Set();
  for (const [w, h] of dims) {
    if (chosen.length >= need) break;
    if (distinct && (usedSides.has(w) || usedSides.has(h))) continue;
    chosen.push([w, h]);
    usedSides.add(w);
    usedSides.add(h);
  }
  if (chosen.length < need) return [];

  // Shelf packing: fill the row left to right, and drop to a new shelf below when the next rectangle
  // no longer fits. Laying them out side by side only worked when the widths happened to fit on one
  // row — "two rectangles of perimeter 18" on a 15x10 grid gives 8x1 and 7x2, which do not, so the
  // whole reveal was abandoned and the "Correct solution" showed an empty grid.
  const ids = [];
  let col = 0, row = 0, shelfH = 0;
  for (const [w, h] of chosen) {
    if (col + w > cols) {
      row += shelfH + 1;
      col = 0;
      shelfH = 0;
    }
    if (col + w > cols || row + h > rows) return [];
    for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) ids.push(byCell.get(`${col + c},${row + r}`));
    col += w + 1;
    shelfH = Math.max(shelfH, h);
  }
  return ids.every(Boolean) ? ids : [];
};

// For a polygon "sketch N rectangles by area" question, lay one example rectangle per target area on
// the grid (h = smallest divisor of the area keeping w ≤ cols), stacked vertically with a 1-cell gap.
// Returns a flat corner list (4 pts per rectangle) the canvas groups into quads via shape_count.
const multiRectReveal = (spec) => {
  const areas = Array.isArray(spec.target_areas) ? spec.target_areas.map(Number).filter((n) => n > 0) : [];
  if (!areas.length) return [];
  const cols = Math.max(1, Math.round(Number(spec.grid?.cols) || 8));
  const rows = Math.max(1, Math.round(Number(spec.grid?.rows) || 8));
  const dims = areas.map((a) => {
    for (let h = 1; h <= rows; h++) {
      if (a % h === 0 && a / h <= cols) return [a / h, h];
    }
    return null;
  });
  if (dims.some((d) => !d)) return [];
  const totalH = dims.reduce((s, [, h]) => s + h, 0) + (dims.length - 1);
  if (totalH > rows) return [];
  const pts = [];
  let y = 0;
  for (const [w, h] of dims) {
    pts.push([0, y], [w, y], [w, y + h], [0, y + h]);
    y += h + 1;
  }
  return pts;
};

// Derives the reveal value (correct answer to draw) for an InteractiveDiagramCanvas from a full
// interactive spec (with target_* fields). Shared by take, review and result pages.
export const interactiveRevealValue = (spec) => {
  if (!spec) return null;
  if (spec.kind === "polygon" && Array.isArray(spec.target_areas) && spec.target_areas.length) {
    return multiRectReveal(spec);
  }
  if (spec.kind === "shade") {
    // Multi-group ("complete the pie chart"): the answer is an ASSIGNMENT of region -> category, so the
    // reveal is [{id, group}, …] rather than a flat id list. Lay each category out as one contiguous run
    // over the regions the student was free to colour (`given` ones are the worked example already in
    // the picture), in spec order — that reproduces the mark scheme's sectors. Grading accepts any
    // contiguous run of the right size, so this is one valid solution, drawn with the same per-category
    // colours the student worked in.
    if (Array.isArray(spec.groups) && spec.groups.length) {
      const free = (spec.regions || []).filter((r) => !r.given).map((r) => String(r.id));
      const out = [];
      let i = 0;
      for (const g of spec.groups) {
        const n = Number(g.count) || 0;
        for (let k = 0; k < n && i < free.length; k++, i++) {
          out.push({ id: free[i], group: String(g.key) });
        }
      }
      return out;
    }
    if (Array.isArray(spec.target_regions) && spec.target_regions.length) return spec.target_regions.map(String);
    // "any one of several arrangements" (target_sets) — reveal the first accepted set as one valid example.
    if (Array.isArray(spec.target_sets) && spec.target_sets.length) return (spec.target_sets[0] || []).map(String);
    // count mode ("shade any N") carries no fixed target set — reveal the first N regions as one valid example.
    if (spec.target_mode === "count" && spec.target_count > 0) {
      return (spec.regions || []).slice(0, spec.target_count).map((r) => String(r.id));
    }
    // polyomino ("draw pattern N") — reveal the target cells by mapping each [col,row] to its region id.
    if (spec.target_mode === "polyomino" && Array.isArray(spec.target_shape)) {
      const byCell = new Map((spec.regions || []).map((r) => [`${r.cell?.[0]},${r.cell?.[1]}`, String(r.id)]));
      return spec.target_shape.map(([c, r]) => byCell.get(`${c},${r}`)).filter(Boolean);
    }
    // metrics ("any shape with this area/perimeter") — reveal one valid example: the first w×h
    // rectangle on the grid whose cell count and boundary match the targets.
    if (spec.target_mode === "metrics") return metricsRevealExample(spec);
    // ways ("show ALL the ways" on several printed grid copies) — reveal way i drawn on copy i.
    if (spec.target_mode === "ways" && Array.isArray(spec.target_ways)) {
      const byGridCell = new Map(
        (spec.regions || []).map((r) => [`${r.grid ?? 0}|${r.cell?.[0]},${r.cell?.[1]}`, String(r.id)])
      );
      return spec.target_ways.flatMap((way, g) =>
        (way || []).map(([c, r]) => byGridCell.get(`${g}|${c},${r}`)).filter(Boolean)
      );
    }
    return [];
  }
  if (spec.kind === "lines") return spec.target_lines || [];
  // cut-drag: reveal the canonical cut with the left piece slid by the shape's base vector — the
  // assembled target shape.
  if (spec.kind === "cut-drag") {
    const s = Array.isArray(spec.shape) ? spec.shape : [];
    if (!Array.isArray(spec.target_cut) || s.length < 4) return null;
    const b = [Number(s[2][0]) - Number(s[3][0]), Number(s[2][1]) - Number(s[3][1])];
    return { cut: spec.target_cut, offsets: [b, [0, 0]] };
  }
  if (spec.kind === "segments") {
    const nodes = Array.isArray(spec.nodes) ? spec.nodes : [];
    const segs = (Array.isArray(spec.target_segment_sets) && spec.target_segment_sets[0])
      ? spec.target_segment_sets[0]
      : (spec.target_segments || []);
    return segs
      .map(([i, j]) => (nodes[i] && nodes[j] ? [nodes[i], nodes[j]] : null))
      .filter(Boolean);
  }
  if (spec.kind === "pictogram") {
    const count = Math.max(0, Math.round(Number(spec.row?.count) || 0));
    const t = Number(spec.target_icons) || 0;
    const full = Math.floor(t + 1e-9), frac = Math.round((t - full) * 4) / 4;
    return Array.from({ length: count }, (_, i) => (i < full ? 1 : i === full ? frac : 0));
  }
  if (spec.kind === "glyph") return [...(spec.targets || []), ...(spec.optional_targets || [])];
  if (spec.kind === "clock") return clockRevealHands(spec);
  // A marker answer is normally a VALUE on the axis (target_value, e.g. 0.5 on a 0..1 probability scale),
  // not a literal point — target_point is null on those. Revealing target_point alone drew an EMPTY scale
  // for the "Correct solution". Project the value back onto the axis to get the point to show.
  if (spec.kind === "marker") {
    const axis = spec.axis;
    const hasAxis = Array.isArray(axis) && axis.length >= 2 && Array.isArray(axis[0]) && Array.isArray(axis[1]);
    // Multi-bar ("complete the bar chart"): each column's own target height, at that column's x.
    if (hasAxis && Array.isArray(spec.bars) && spec.bars.length) {
      const v0 = Number(spec.v0 ?? 0);
      const v1 = Number(spec.v1 ?? 1);
      const span = v1 - v0;
      const [a, b] = axis;
      return spec.bars
        .filter((bar) => bar && Number.isFinite(Number(bar.x)) && bar.target_value != null)
        .map((bar) => {
          const t = span === 0 ? 0 : Math.max(0, Math.min(1, (Number(bar.target_value) - v0) / span));
          return [Number(bar.x), a[1] + t * (b[1] - a[1])];
        });
    }
    if (Array.isArray(spec.target_point)) return [spec.target_point];
    if (!hasAxis || spec.target_value == null) return [];
    const v0 = Number(spec.v0 ?? 0);
    const v1 = Number(spec.v1 ?? 1);
    const span = v1 - v0;
    const t = span === 0 ? 0 : Math.max(0, Math.min(1, (Number(spec.target_value) - v0) / span));
    const [a, b] = axis;
    return [[a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]];
  }
  // Open lines anchored to a GIVEN point (e.g. "join K to L" where K is context_points, never plotted):
  // the plain target_points list the generic fallback below reveals doesn't include K at all, so the
  // canvas's own point-to-point auto-join (there being no drawn edges to show instead) chains whatever
  // IS revealed in array order — wrongly drawing L to the reflected line's first point instead of K to L,
  // and never drawing K to L at all. required_edges already names the exact lines to draw, so reveal
  // them as real edges (same "gx,gy|gx,gy" keys InteractiveDiagramCanvas.jsx's edgeKey() produces).
  // Also closed "complete the shape" figures whose outline is partly PRINTED (given_points + the
  // reflected corner): the model answer is the corner plus exactly the joining edges, never a full ring.
  if (
    spec.kind === "points"
    && Array.isArray(spec.required_edges) && spec.required_edges.length > 0
  ) {
    const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const edgeKey = (a, b) => [cornerKey(a), cornerKey(b)].sort().join("|");
    const edges = spec.required_edges
      .filter((pair) => Array.isArray(pair) && Array.isArray(pair[0]) && Array.isArray(pair[1]))
      .map(([a, b]) => edgeKey(a, b));
    const out = { points: spec.target_points || [], edges };
    if (spec.require_shape_label && typeof spec.shape_label === "string" && spec.shape_label.trim() !== "") {
      out.shape_label = spec.shape_label.trim();
    }
    return out;
  }
  // "Plot D and draw the parallelogram ABCD": A/B/C are given (context_points) and only D is plotted, so
  // target_points is just D — revealing that alone shows a lone dot and no shape. The model answer is the
  // whole closed figure, so reveal the full boundary (given corners + the plotted one) in boundary order.
  let pts = spec.target_points || [];
  const printedPts = [
    ...(Array.isArray(spec.context_points) ? spec.context_points : []),
    ...(Array.isArray(spec.given_points) ? spec.given_points : []),
  ];
  if (
    (spec.kind === "points" || spec.kind === "polygon")
    && spec.closed !== false
    && spec.connect !== false
    && printedPts.length > 0
    && Array.isArray(spec.target_points)
    && printedPts.length + spec.target_points.length >= 3
  ) {
    pts = orderClosedBoundary([...printedPts, ...spec.target_points]);
  }
  // "Label the triangle A" / "Label your shape B": reveal carries the shape letter on the value object so
  // the canvas can paint it at the centroid (same channel the student answer uses).
  if (spec.require_shape_label && typeof spec.shape_label === "string" && spec.shape_label.trim() !== "") {
    return { points: pts, shape_label: spec.shape_label.trim() };
  }
  return pts;
};
